/**
 * Figma (mobile): https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=6961-334952&m=dev
 * Figma (desktop grid): https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=6961-335592&m=dev
 */

import { useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { Icon } from '@susu/ui/components'
import { useQuery } from '@tanstack/react-query'

import { buildWardrobeImageUrl, fetchWardrobe } from '../../api/wardrobeApi'
import { ROLE, type Role } from '../../contexts/CustomerHubContext'
import { useModal } from '../../contexts/ModalContext'
import { useSkeletonPhase } from '../../hooks/useSkeletonPhase'
import { cn } from '../../lib/utils'
import { normalizeEnvironment } from '../../utils/normalizeEnv'
import {
  getWardrobeGroupKey,
  getWardrobeItemDisplayName,
  getWardrobeSectionLabel,
  isCustomMadeItem,
} from '../../utils/wardrobe'
import EmptyState from '../EmptyState'
import Skeleton from '../Skeleton'

import WardrobeItemModal from './WardrobeItemModal'

import type { EnvironmentWithChina } from '../../App'
import type { GroupedWardrobeItem, WardrobeItem } from '../../types/wardrobe'

// Desktop gets a wider column than the other tabs' 490/524/574px cap so the
// grid fits three cards per row (Claude Design "Wardrobe Grid Final 3-up").
const WARDROBE_CONTAINER_CLASS =
  'flex w-full flex-col gap-48 px-16 pt-24 pb-80 md:max-w-[490px] md:px-0 md:max-lg:mx-auto lg:max-w-[800px]'
const WARDROBE_GRID_CLASS =
  'grid grid-cols-2 gap-x-12 gap-y-32 lg:grid-cols-3 lg:gap-x-16'

interface WardrobeTabProps {
  accountNumber: string
  env: EnvironmentWithChina
  authToken?: string
  role?: Role
  /**
   * Country the customer is signed in on, from `getResolvedCountryCode`.
   * Decides which of the item's regional sizes the item modal shows.
   */
  countryCode?: string
  onShopNewArrivals?: () => void
  onItemClicked?: (
    section: string,
    itemCode: string,
    isCustomMade: boolean,
  ) => void
}

function groupBySku(items: WardrobeItem[]): GroupedWardrobeItem[] {
  const groups = new Map<string, WardrobeItem[]>()
  for (const item of items) {
    const key = item.sku ?? item.custom_made?.configuration_id ?? item.item_code
    const group = groups.get(key)
    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }

  return Array.from(groups.values()).map((group) => {
    const latest = group.reduce((a, b) =>
      new Date(a.purchased_at) > new Date(b.purchased_at) ? a : b,
    )
    return { ...latest, quantity: group.length }
  })
}

/**
 * Sections come out in payload order, which the trait emits in merchandising
 * order (RP-5934) — a Map preserves insertion order, so there is deliberately
 * no sort here. Reordering the wardrobe is a trait-side change.
 */
function groupBySection(
  items: GroupedWardrobeItem[],
): Map<string, GroupedWardrobeItem[]> {
  const groups = new Map<string, GroupedWardrobeItem[]>()
  for (const item of items) {
    const key = getWardrobeGroupKey(item)
    const group = groups.get(key)
    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }
  return groups
}

interface WardrobeProductCardProps {
  item: GroupedWardrobeItem
  onClick: () => void
}

function WardrobeProductCard({ item, onClick }: WardrobeProductCardProps) {
  const [imageError, setImageError] = useState(false)
  const imageUrl = buildWardrobeImageUrl(item.image_uri)
  const isCustomMade = isCustomMadeItem(item)
  const displayName = getWardrobeItemDisplayName(item)
  const showImage = Boolean(imageUrl) && !imageError
  const hasMultiple = item.quantity > 1

  return (
    <button
      type="button"
      className="flex flex-col gap-10 items-start text-left cursor-pointer"
      onClick={onClick}
      data-testid="wardrobe-product-card"
    >
      <div className="relative w-full aspect-[5/6]">
        {hasMultiple && (
          <div className="absolute inset-0 -rotate-3 overflow-hidden rounded-sm bg-background-gray-dark" />
        )}

        <div className="absolute inset-0 bg-background-gray-dark overflow-hidden rounded-sm flex items-center justify-center p-30">
          {showImage ? (
            <img
              src={imageUrl}
              alt={displayName}
              loading="lazy"
              decoding="async"
              onError={() => setImageError(true)}
              className="w-full h-full object-contain"
            />
          ) : (
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>No image</title>
              <rect width="40" height="40" rx="4" fill="#E5E5E5" />
              <path d="M12 28L17 23L21 27L26 22L28 24V28H12Z" fill="#C6C6C6" />
              <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
            </svg>
          )}

          {isCustomMade && (
            <div className="backdrop-blur-sm absolute top-[6px] left-[6px] lg:top-12 lg:left-12 flex h-[22px] w-[22px] items-center justify-center rounded-md bg-white-alpha-600">
              <Icon name="custom-made" size={14} />
            </div>
          )}
        </div>
      </div>

      <p className="w-full pr-12 text-sm leading-sm font-medium text-text overflow-hidden text-ellipsis line-clamp-2">
        {displayName}
      </p>
    </button>
  )
}

export default function WardrobeTab({
  accountNumber,
  env,
  authToken,
  role,
  countryCode,
  onShopNewArrivals,
  onItemClicked,
}: WardrobeTabProps) {
  const { t } = useI18N()
  const { openModal } = useModal()
  const baseEnv = normalizeEnvironment(env)

  const {
    data: wardrobeItems = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['wardrobe', accountNumber, baseEnv],
    queryFn: () => fetchWardrobe(accountNumber, env, authToken),
    enabled: Boolean(accountNumber),
    staleTime: 5 * 60 * 1000,
  })

  const { phase, contentClassName } = useSkeletonPhase(isLoading)

  // Exact-layout grid skeleton in the tab's own container (invisible for the
  // first 150ms, so a fast/cached response never flashes it).
  if (phase !== 'content') {
    return (
      <div
        className={cn(
          WARDROBE_CONTAINER_CLASS,
          phase === 'reserve' && 'invisible',
        )}
        aria-busy="true"
        data-testid="wardrobe-skeleton"
      >
        <section className="flex flex-col">
          {/* Prototype: category heading bar 120×24, one row of tiles. */}
          <div className="mb-12">
            <Skeleton w={120} h={24} />
          </div>
          <div className={WARDROBE_GRID_CLASS}>
            {Array.from({ length: 3 }, (_, index) => (
              <div
                key={index}
                className={cn(
                  'flex flex-col items-start gap-10',
                  index === 2 && 'max-lg:hidden',
                )}
              >
                <Skeleton ar="5/6" className="w-full" />
                <Skeleton h={14} className="w-[70%]" />
              </div>
            ))}
          </div>
        </section>
      </div>
    )
  }

  if (isError || wardrobeItems.length === 0) {
    const isStyleAdvisor = role === ROLE.STYLE_ADVISOR
    const emptyTitle = isStyleAdvisor
      ? t('wardrobe.emptyStyleAdvisor')
      : t('wardrobe.emptyCustomer')

    return (
      <div className={contentClassName}>
        <EmptyState
          testId="wardrobe-empty-state"
          title={emptyTitle}
          primaryAction={
            !isStyleAdvisor && onShopNewArrivals
              ? {
                  label: t('actions.shopNewArrivals'),
                  onClick: onShopNewArrivals,
                  testId: 'wardrobe-shop-new-arrivals-button',
                }
              : undefined
          }
        />
      </div>
    )
  }

  const grouped = groupBySection(groupBySku(wardrobeItems))

  return (
    <div
      className={cn(WARDROBE_CONTAINER_CLASS, contentClassName)}
      data-testid="wardrobe-tab"
    >
      {Array.from(grouped.entries()).map(([section, items]) => (
        <section key={section} className="flex flex-col">
          <h2 className="text-xl leading-xl font-medium text-text tracking-heading pb-12">
            {getWardrobeSectionLabel(section, t)}
          </h2>
          <div className={WARDROBE_GRID_CLASS}>
            {items.map((item, index) => (
              <WardrobeProductCard
                key={`${item.sku ?? item.item_code}-${index}`}
                item={item}
                onClick={() => {
                  onItemClicked?.(
                    section,
                    item.item_code,
                    isCustomMadeItem(item),
                  )
                  openModal(
                    <WardrobeItemModal item={item} countryCode={countryCode} />,
                  )
                }}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
